import React, { useMemo, useState } from "react";
import { Play, Youtube } from "lucide-react";
import { VIDEOS } from "../../lib/content";
import VideoLightbox from "./VideoLightbox";

// Service-page video strip — same dark-band visual as HomeVideoStrip,
// filtered to videos tagged with the current service slug (via `services`
// array on the VIDEO entry). Shows up to 3; hides when there's nothing to
// show. For a single video the card scales up so the band doesn't look
// sparse.
export default function ServiceVideoStrip({
  service,
  eyebrow = "From the field",
  heading = "Shorts from the installs behind this service.",
}) {
  const [active, setActive] = useState(null);

  const items = useMemo(() => {
    return VIDEOS
      .filter((v) => Array.isArray(v.services) && v.services.includes(service))
      .sort((a, b) => (b.date || "").localeCompare(a.date || ""))
      .slice(0, 3);
  }, [service]);

  if (items.length === 0) return null;

  // Grid column count adapts to how many clips we have.
  const cols = Math.min(items.length, 3);
  const gridClass = cols === 1
    ? "grid grid-cols-1 max-w-[720px] mx-auto"
    : cols === 2
    ? "grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6"
    : "grid grid-cols-3 gap-2 sm:gap-3 md:gap-4";

  return (
    <section
      className="px-6 md:px-12 lg:px-20 py-12 md:py-16"
      style={{ background: "#070A0D", color: "#F5F7FA" }}
      data-testid={`service-video-strip-${service}`}
    >
      <div className="max-w-[1500px] mx-auto">
        <div className="mb-6 md:mb-8">
          <div
            className="font-mono uppercase inline-flex items-center gap-2 mb-2"
            style={{ fontSize: "0.6rem", letterSpacing: "0.28em", color: "#1FB6C1" }}
          >
            <Youtube size={11} aria-hidden /> {eyebrow}
          </div>
          <h2
            className="font-display"
            style={{
              fontSize: "clamp(1.3rem, 2.4vw, 1.9rem)",
              lineHeight: 1.15,
              letterSpacing: "-0.015em",
              fontWeight: 400,
              maxWidth: "34ch",
            }}
          >
            {heading}
          </h2>
        </div>

        <div className={gridClass} data-testid={`service-video-grid-${service}`}>
          {items.map((v) => (
            <ServiceVideoCard
              key={v.slug}
              video={v}
              single={cols === 1}
              onPlay={() => setActive(v)}
            />
          ))}
        </div>
      </div>

      <VideoLightbox video={active} onClose={() => setActive(null)} />
    </section>
  );
}

function ServiceVideoCard({ video: v, single, onPlay }) {
  const isPortrait = v.orientation === "portrait";
  const poster =
    v.poster ||
    (v.youtube_id ? `https://i.ytimg.com/vi/${v.youtube_id}/hqdefault.jpg` : "/library/purification/purification_01.webp");

  // When this strip has a single video, let the thumbnail breathe —
  // bigger on desktop, caption on the right (same two-column pattern).
  const thumbWidthClass = single
    ? "w-full md:w-[260px] lg:w-[300px]"
    : "w-full md:w-[170px] lg:w-[210px]";

  return (
    <div className="flex md:flex-row flex-col gap-3 md:gap-5 items-stretch min-w-0">
      <button
        type="button"
        onClick={onPlay}
        className={`group relative block overflow-hidden text-left transition-transform duration-300 hover:-translate-y-1 shrink-0 ${thumbWidthClass}`}
        style={{
          borderRadius: 6,
          aspectRatio: isPortrait ? "9 / 16" : "16 / 9",
          background: "#0C2A40",
          border: "1px solid rgba(255,255,255,0.06)",
        }}
        data-testid={`service-video-card-${v.slug}`}
        aria-label={`Play video: ${v.title}`}
      >
        <img
          src={poster}
          alt=""
          loading="lazy"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          onError={(e) => { e.currentTarget.src = "/library/purification/purification_01.webp"; }}
        />
        <div
          className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(7,10,13,0.10) 30%, rgba(7,10,13,0.78) 100%)" }}
          aria-hidden
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            className="flex items-center justify-center transition-transform duration-300 group-hover:scale-110"
            style={{
              width: single ? 54 : 44,
              height: single ? 54 : 44,
              borderRadius: 999,
              background: "rgba(255,255,255,0.96)",
              boxShadow: "0 6px 18px rgba(0,0,0,0.40)",
            }}
          >
            <Play size={single ? 20 : 16} fill="#0C2A40" color="#0C2A40" style={{ marginLeft: 2 }} aria-hidden />
          </div>
        </div>

        {v.duration && (
          <div
            className="absolute top-2 right-2 px-1.5 py-0.5 text-[0.6rem] font-semibold"
            style={{
              background: "rgba(7,10,13,0.85)",
              color: "#F5F7FA",
              borderRadius: 3,
              letterSpacing: "0.02em",
            }}
          >
            {v.duration}
          </div>
        )}

        {/* MOBILE-ONLY title overlay */}
        <div className="md:hidden absolute left-2.5 right-2.5 bottom-2.5">
          <div
            className="font-display text-white"
            style={{
              fontSize: "0.78rem",
              lineHeight: 1.25,
              fontWeight: 600,
              letterSpacing: "-0.005em",
              textShadow: "0 2px 12px rgba(0,0,0,0.7)",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {v.title}
          </div>
          {v.filmed_at && (
            <div
              className="mt-1 font-mono uppercase"
              style={{
                fontSize: "0.5rem",
                letterSpacing: "0.18em",
                color: "rgba(245,247,250,0.78)",
              }}
            >
              {v.filmed_at}
            </div>
          )}
        </div>
      </button>

      {/* DESKTOP caption column */}
      <div className="hidden md:flex flex-col justify-start flex-1 min-w-0 pr-2 pt-1">
        {v.filmed_at && (
          <div
            className="font-mono uppercase mb-2"
            style={{
              fontSize: single ? "0.6rem" : "0.54rem",
              letterSpacing: "0.22em",
              color: "#1FB6C1",
            }}
          >
            {v.filmed_at}
          </div>
        )}
        <h3
          className="font-display mb-2"
          style={{
            fontSize: single ? "1.15rem" : "0.95rem",
            lineHeight: 1.25,
            letterSpacing: "-0.005em",
            color: "#F5F7FA",
            fontWeight: 600,
          }}
        >
          {v.title}
        </h3>
        <p
          className="font-light"
          style={{
            fontSize: single ? "0.86rem" : "0.78rem",
            lineHeight: 1.6,
            color: "rgba(245,247,250,0.72)",
          }}
        >
          {v.caption}
        </p>
      </div>
    </div>
  );
}
